import { useState } from 'react';
import {
  ETIQUETAS_CONDICION_PAGO,
  type ModoFacturacion,
} from '@lexicore/shared';
import { IconoAlerta, IconoCerrar } from '../../componentes/Iconos';
import { useAvisos } from '../../contextos/AvisosContext';
import type { ProyectoDetalle } from '../../lib/consultas';
import { useRegenerarCronograma } from '../../lib/mutaciones';

interface Props {
  proyecto: ProyectoDetalle;
  alCerrar: () => void;
}

export function ModalConfigurarCronograma({ proyecto, alCerrar }: Props) {
  const avisos = useAvisos();
  const regenerarMut = useRegenerarCronograma();

  const [modo, setModo] = useState<ModoFacturacion>(
    (proyecto.modoFacturacion as ModoFacturacion) ?? 'AL_CIERRE',
  );
  const [duracionMeses, setDuracionMeses] = useState<number>(12);
  const [condicionPago, setCondicionPago] = useState<string>('');

  const cuotasFacturadas = proyecto.cuotas.filter((c) => c.estado === 'FACTURADA').length;

  const ejecutar = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await regenerarMut.mutateAsync({
        proyectoId: proyecto.id,
        modo,
        duracionMeses: modo === 'MENSUAL' ? duracionMeses : undefined,
        condicionPago: condicionPago || undefined,
      });

      avisos.exito('Cronograma de facturación configurado y actualizado correctamente.');
      alCerrar();
    } catch (err) {
      avisos.error(
        err instanceof Error ? err.message : 'No se pudo configurar el cronograma.',
      );
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && alCerrar()}
      role="dialog"
      aria-modal="true"
    >
      <div className="modal" style={{ maxWidth: 620 }}>
        <div className="modal-header">
          <div>
            <h2>Configurar / Regenerar Facturación</h2>
            <p style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
              Proyecto: <strong>{proyecto.codigo}</strong> · {proyecto.nombre}
            </p>
          </div>
          <button
            type="button"
            className="btn-icono"
            onClick={alCerrar}
            aria-label="Cerrar modal"
          >
            <IconoCerrar size={14} />
          </button>
        </div>

        <form onSubmit={ejecutar}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {cuotasFacturadas > 0 && (
              <div
                style={{
                  padding: '11px 14px',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--accent-blue-soft)',
                  color: 'var(--text-primary)',
                  fontSize: 12,
                  lineHeight: 1.5,
                  display: 'flex',
                  gap: 8,
                }}
              >
                <IconoAlerta size={16} style={{ flexShrink: 0, marginTop: 1, color: 'var(--accent-blue)' }} />
                <div>
                  Hay <strong>{cuotasFacturadas} cuota(s) ya facturada(s)</strong>. Se conservarán intactas y el monto restante del contrato se distribuirá equitativamente entre las cuotas proyectadas.
                </div>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="selectModoFacturacion">Tipo o Modo de Facturación</label>
              <select
                id="selectModoFacturacion"
                value={modo}
                onChange={(e) => setModo(e.target.value as ModoFacturacion)}
                style={{ width: '100%' }}
              >
                <option value="AL_CIERRE">Factura Única al Término / Cierre</option>
                <option value="CONTRA_OC">Factura Única contra Orden de Compra</option>
                <option value="MENSUAL">Contrato Mensual Recurrente (Plazo en Meses)</option>
                <option value="POR_HITOS">Facturación por Hitos Técnicos</option>
              </select>
            </div>

            {modo === 'MENSUAL' && (
              <div
                style={{
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '14px 16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                <label style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)' }}>
                  Duración del Contrato en Meses
                </label>

                {/* Botones de Selección Rápida */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {[
                    { label: '12 Meses (1 año)', val: 12 },
                    { label: '24 Meses (2 años)', val: 24 },
                    { label: '36 Meses (3 años)', val: 36 },
                    { label: '48 Meses (4 años)', val: 48 },
                  ].map((preset) => (
                    <button
                      key={preset.val}
                      type="button"
                      className={`btn btn-sm ${duracionMeses === preset.val ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setDuracionMeses(preset.val)}
                      style={{ fontSize: 11.5 }}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>

                <div className="form-group" style={{ marginTop: 6 }}>
                  <label htmlFor="inputMesesPersonalizado" style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                    O especifica una cantidad exacta de meses (1 a 120):
                  </label>
                  <input
                    id="inputMesesPersonalizado"
                    type="number"
                    min={1}
                    max={120}
                    value={duracionMeses}
                    onChange={(e) => setDuracionMeses(Math.max(1, parseInt(e.target.value, 10) || 1))}
                    style={{ width: 140, fontFamily: 'var(--font-mono)' }}
                  />
                </div>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="selectCondicionPago">Condición de Pago (Opcional)</label>
              <select
                id="selectCondicionPago"
                value={condicionPago}
                onChange={(e) => setCondicionPago(e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="">Por defecto de la empresa</option>
                {Object.entries(ETIQUETAS_CONDICION_PAGO).map(([clave, etiqueta]) => (
                  <option key={clave} value={clave}>
                    {etiqueta}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={alCerrar}
              disabled={regenerarMut.isPending}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={regenerarMut.isPending}
            >
              {regenerarMut.isPending ? 'Guardando…' : 'Aplicar y Regenerar Cronograma'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
